import { Service } from '@angular/core'

/** Where a premise's dismissal is kept: one key per premise, holding the version that was closed. */
export const announcementDismissalKey = (entityId: string) => `seiri:announcement:${entityId}`

type DismissalStorage = Pick<Storage, 'getItem' | 'setItem'>

/**
 * Whether the visitor closed this version of the announcement. A dismissal holds only for the version it was made
 * on (`PublicAnnouncement.updatedAt`), so an edited announcement shows again. Storage that is missing or throws (a
 * private window, blocked site data) reads as not dismissed: the announcement shows rather than vanishing.
 */
export function readDismissal(storage: DismissalStorage | null, entityId: string, version: string): boolean {
  try {
    return storage?.getItem(announcementDismissalKey(entityId)) === version
  } catch {
    return false
  }
}

/** Records the dismissal. Storage that throws loses it, and the announcement then shows again on the next page. */
export function writeDismissal(storage: DismissalStorage | null, entityId: string, version: string): void {
  try {
    storage?.setItem(announcementDismissalKey(entityId), version)
  } catch {
    // Nothing to do: the caller still hides it for this page view.
  }
}

/**
 * The visitor's dismissals, for the browser session: `sessionStorage`, so a closed announcement stays closed across
 * reloads in that tab and comes back in a new one. docs/announcements.md has the rule.
 */
@Service()
export class AnnouncementDismissal {
  isDismissed(entityId: string, version: string): boolean {
    return readDismissal(sessionStorageOrNull(), entityId, version)
  }

  dismiss(entityId: string, version: string): void {
    writeDismissal(sessionStorageOrNull(), entityId, version)
  }
}

/** Reading `sessionStorage` itself throws where site data is blocked. */
function sessionStorageOrNull(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}
